import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createMelodySequence,
  isMelodyMatch,
  isMelodyPrefix,
  TUCHI_MELODY_LENGTH,
  TUCHI_MELODY_TIME_LIMIT_SEC,
  TUCHI_NOTE_COUNT,
  TUCHI_VICTORY_HOLD_MS,
} from '../../../../domain';
import {
  TUCHI_BUTTON_LISTEN_URL,
  TUCHI_FRAME_NOTE_URLS,
  TUCHI_FRAME_URL,
  TUCHI_NOTE_URLS,
  TUCHI_TILE_URL,
  TUCHI_TIMER_URL,
  TUCHI_VICTORY_URL,
} from '../assets';
import {
  resumeBackgroundMusic,
  suspendBackgroundMusic,
} from '../../../assets/preloadSounds';
import {
  ensureTuchiAudioReady,
  playTuchiMarimbaFail,
  playTuchiMarimbaNote,
  playTuchiMarimbaVictory,
} from './tuchiMarimbaAudio';
import './TuchiMelodyGame.css';

export type TuchiMelodyGameProps = {
  onWin: () => void;
  onLose: () => void;
  onTimerChange?: (secondsLeft: number) => void;
};

type Phase = 'play' | 'victory';
type NoteId = 1 | 2 | 3 | 4;

const NOTE_FLASH_MS = 320;
const NOTE_GAP_MS = 140;

const FRAME_SLOTS = [
  { left: 171.52, top: 624.279 },
  { left: 296.605, top: 624.279 },
  { left: 421.203, top: 624.279 },
  { left: 545.637, top: 624.279 },
  { left: 670.719, top: 624.279 },
  { left: 795.156, top: 624.279 },
] as const;

const NOTE_LAYOUT: { id: NoteId; left: number; top: number }[] = [
  { id: 1, left: 229.16, top: 944.809 },
  { id: 2, left: 553.887, top: 944.809 },
  { id: 3, left: 229.16, top: 1142.81 },
  { id: 4, left: 554, top: 1142.81 },
];

function formatTuchiTimer(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} сек`;
}

function asNoteId(note: number): NoteId | null {
  if (note >= 1 && note <= TUCHI_NOTE_COUNT) {
    return note as NoteId;
  }
  return null;
}

export function TuchiMelodyGame({
  onWin,
  onLose,
  onTimerChange,
}: TuchiMelodyGameProps) {
  const melody = useMemo(() => createMelodySequence(), []);
  const [input, setInput] = useState<number[]>([]);
  const [secondsLeft, setSecondsLeft] = useState<number>(TUCHI_MELODY_TIME_LIMIT_SEC);
  const [litNote, setLitNote] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [phase, setPhase] = useState<Phase>('play');
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    // Melody needs a quiet bed — pause level BGM for the whole minigame.
    suspendBackgroundMusic();
    return () => {
      resumeBackgroundMusic();
    };
  }, []);

  useEffect(() => {
    onTimerChange?.(secondsLeft);
  }, [onTimerChange, secondsLeft]);

  useEffect(() => {
    if (phase !== 'play' || finished) {
      return;
    }
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finished, phase]);

  useEffect(() => {
    if (finished || phase !== 'play' || secondsLeft > 0) {
      return;
    }
    setFinished(true);
    onLose();
  }, [finished, onLose, phase, secondsLeft]);

  useEffect(() => {
    if (phase !== 'victory') {
      return;
    }
    playTuchiMarimbaVictory();
    const timer = window.setTimeout(() => {
      onWin();
    }, TUCHI_VICTORY_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [onWin, phase]);

  const playMelody = useCallback(async () => {
    if (playing || finished || phase !== 'play') {
      return;
    }
    await ensureTuchiAudioReady();
    setPlaying(true);
    setInput([]);
    for (const note of melody) {
      setLitNote(note);
      playTuchiMarimbaNote(note, NOTE_FLASH_MS / 1000);
      await new Promise((r) => window.setTimeout(r, NOTE_FLASH_MS));
      setLitNote(null);
      await new Promise((r) => window.setTimeout(r, NOTE_GAP_MS));
    }
    setPlaying(false);
  }, [finished, melody, phase, playing]);

  const failAttempt = useCallback(() => {
    playTuchiMarimbaFail();
    setFinished(true);
    onLose();
  }, [onLose]);

  const pressNote = useCallback(
    (note: number) => {
      if (playing || finished || phase !== 'play') {
        return;
      }
      void ensureTuchiAudioReady();
      setLitNote(note);
      playTuchiMarimbaNote(note, NOTE_FLASH_MS / 1000);
      window.setTimeout(() => {
        setLitNote((current) => (current === note ? null : current));
      }, NOTE_FLASH_MS);

      const nextInput = [...input, note];
      if (!isMelodyPrefix(melody, nextInput)) {
        failAttempt();
        return;
      }
      setInput(nextInput);
      if (isMelodyMatch(melody, nextInput)) {
        setFinished(true);
        setPhase('victory');
      }
    },
    [failAttempt, finished, input, melody, phase, playing],
  );

  const controlsDisabled = playing || finished || phase !== 'play';

  return (
    <div className="tuchi-melody">
      <img className="tuchi-melody__tile" src={TUCHI_TILE_URL} alt="" draggable={false} />
      <h2 className="tuchi-melody__title">Собери мелодию!</h2>
      <p className="tuchi-melody__hint">Заполни {TUCHI_MELODY_LENGTH} ячеек нотами</p>

      {FRAME_SLOTS.map((slot, index) => {
        const filled = asNoteId(input[index] ?? 0);
        return (
          <div
            key={`frame-${index}`}
            className="tuchi-melody__frame"
            style={{ left: slot.left, top: slot.top }}
          >
            <img className="tuchi-melody__frame-art" src={TUCHI_FRAME_URL} alt="" draggable={false} />
            {filled ? (
              <img
                className="tuchi-melody__frame-note"
                src={TUCHI_FRAME_NOTE_URLS[filled]}
                alt=""
                draggable={false}
              />
            ) : null}
          </div>
        );
      })}

      <button
        type="button"
        className="tuchi-melody__listen"
        onClick={() => {
          void playMelody();
        }}
        disabled={controlsDisabled}
        aria-label="Послушать мелодию"
      >
        <img
          className="tuchi-melody__listen-art"
          src={TUCHI_BUTTON_LISTEN_URL}
          alt=""
          draggable={false}
        />
      </button>

      {NOTE_LAYOUT.map((note) => (
        <button
          key={note.id}
          type="button"
          className={`tuchi-melody__note${litNote === note.id ? ' is-lit' : ''}`}
          style={{ left: note.left, top: note.top }}
          onClick={() => pressNote(note.id)}
          disabled={controlsDisabled}
          aria-label={`Нота ${note.id}`}
        >
          <img
            className="tuchi-melody__note-art"
            src={TUCHI_NOTE_URLS[note.id]}
            alt=""
            draggable={false}
          />
        </button>
      ))}

      <div className="tuchi-melody__timer">
        <img className="tuchi-melody__timer-art" src={TUCHI_TIMER_URL} alt="" draggable={false} />
        <p className="tuchi-melody__timer-value">{formatTuchiTimer(secondsLeft)}</p>
      </div>

      {phase === 'victory' ? (
        <div className="tuchi-melody__victory" aria-live="polite">
          <img
            className="tuchi-melody__victory-art"
            src={TUCHI_VICTORY_URL}
            alt=""
            draggable={false}
          />
        </div>
      ) : null}
    </div>
  );
}
